import { api, type Warehouse } from '@/api/client'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const STORAGE_KEY = 'amanet.warehouse.site'
const listeners = new Set<(id: string) => void>()

function readStored() {
  return localStorage.getItem(STORAGE_KEY)
}

function writeStored(id: string) {
  localStorage.setItem(STORAGE_KEY, id)
  for (const listener of listeners) listener(id)
}

export function useWarehouseSite() {
  const warehouses = useQuery({
    queryKey: ['warehouse', 'list'],
    queryFn: () => api.warehouse.listWarehouses(),
  })
  const [stored, setStored] = useState<string | null>(() => readStored())
  const list = warehouses.data ?? []
  const warehouse =
    list.find((site) => site.id === stored) ?? list.find((site) => site.is_default) ?? list[0]

  useEffect(() => {
    listeners.add(setStored)
    return () => {
      listeners.delete(setStored)
    }
  }, [])

  useEffect(() => {
    if (!warehouse || warehouse.id === stored) return
    writeStored(warehouse.id)
  }, [warehouse, stored])

  const select = (id: string) => writeStored(id)

  return {
    warehouses: list,
    warehouse,
    select,
    isLoading: warehouses.isLoading,
    isError: warehouses.isError,
  }
}

export function SiteSwitcher() {
  const { t } = useTranslation('warehouse')
  const { warehouses, warehouse, select, isLoading } = useWarehouseSite()

  if (isLoading) {
    return <div className="mb-4 h-11 w-56 animate-pulse rounded-xl bg-muted" />
  }
  if (!warehouse) {
    return <p className="mb-4 text-sm text-muted-foreground">{t('posts.no_warehouse')}</p>
  }

  return (
    <label className="mb-4 flex min-w-0 flex-col gap-1 text-sm sm:mb-0 sm:min-w-56">
      {t('posts.site_switch')}
      <select
        className="min-h-[44px] rounded-xl border border-border bg-background px-3"
        value={warehouse.id}
        onChange={(event) => select(event.target.value)}
      >
        {warehouses.map((site) => (
          <option key={site.id} value={site.id}>
            {siteLabel(site)}
          </option>
        ))}
      </select>
    </label>
  )
}

function siteLabel(site: Warehouse) {
  const place = site.city || site.name
  return site.is_default ? `${site.code} — ${place} *` : `${site.code} — ${place}`
}
